import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    Box,
    Button,
    Chip
} from '@mui/material';
import httpclient from '../../../Utils';
import moment from 'moment'; // Import Moment.js

// Helper function to format frequency for display
const formatFrequency = (frequency) => {
    if (!frequency) return 'Not set';
    switch (frequency) {
        case 'daily': return 'Every Day';
        case 'weekly': return 'Every 1 Week';
        case 'fortnightly': return 'Every 15 Days';
        case 'monthly': return 'Every 30 Days';
        default: return frequency;
    }
};

// Helper function to format time for display using Moment.js
const formatTime = (time) => {
    if (!time) return 'Not set';
    return moment(time, 'HH:mm:ss').format('hh:mm A');
};

const formatRuleName = (ruleName) => {
    if (!ruleName) return "";
    // Convert snake_case to Title Case
    return ruleName
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
};

// Helper function to format rule value based on data_type
const formatRuleValue = (rule) => {
    if (!rule) return 'Not set';

    // Handle boolean data type
    if (rule.data_type === 'boolean') {
        return rule.value === '1' || rule.value === true ? 'Enabled' : 'Disabled';
    }

    // Handle number data type
    if (rule.data_type === 'number') {
        return rule.value;
    }

    // Default case - return value as is
    return rule.value;
};

// Helper function to format status
const formatStatus = (status) => {
    return status === 1 ? 'Enabled' : 'Disabled';
};

const NotificationDetailModal = ({ open, onClose, notification }) => {
    const [predefinedRules, setPredefinedRules] = useState([]);
    const [loadingRules, setLoadingRules] = useState(false);

    // Fetch predefined rules for notification type
    const fetchPredefinedRules = async (notifyTypeId) => {
        if (!notifyTypeId) return;

        setLoadingRules(true);
        try {
            const response = await httpclient.get(`request-response?requestName=lightspeed/notification-type/rules/${notifyTypeId}`);
            if (response.data?.data) {
                setPredefinedRules(response.data.data);
            }
        } catch (error) {
            console.error("Failed to fetch predefined rules", error);
            setPredefinedRules([]);
        } finally {
            setLoadingRules(false);
        }
    };

    useEffect(() => {
        if (open && notification?.notify_type_id) {
            fetchPredefinedRules(notification.notify_type_id);
        }
    }, [open, notification]);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle
                sx={{
                    bgcolor: "primary.main",
                    color: "primary.contrastText",
                    px: 3,
                    py: 2,
                }}
            >
                <Typography id="modal-title" variant="h6" fontWeight="bold">
                    Message Details - {notification?.title}
                </Typography>
            </DialogTitle>
            <DialogContent>
                <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                        Notification Type: {notification?.notify_type}
                    </Typography>

                    <Box display="flex" gap={2} my={2}>
                        <Box>
                            <Typography variant="subtitle2" color="textSecondary">Frequency</Typography>
                            <Typography variant="body1">
                                {formatFrequency(notification?.frequency)}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography variant="subtitle2" color="textSecondary">Send Time</Typography>
                            <Typography variant="body1">
                                {formatTime(notification?.send_time)}
                            </Typography>
                        </Box>
                    </Box>

                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                        Notification Rules:
                    </Typography>
                    {loadingRules ? (
                        <Box display="flex" justifyContent="center" my={2}>
                            <CircularProgress />
                        </Box>
                    ) : notification?.rules && notification.rules.length > 0 ? (
                        <TableContainer component={Paper} variant="outlined">
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Rule Type</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Value</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Message</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {notification.rules.map((rule, index) => {
                                        const predefinedRule = predefinedRules.find(pr => pr.rule_name === rule.rule_type);
                                        const isDisabled = rule.status === 0;

                                        return (
                                            <TableRow
                                                key={index}
                                                sx={{
                                                    backgroundColor: isDisabled ? '#f5f5f5' : 'inherit',
                                                    opacity: isDisabled ? 0.7 : 1
                                                }}
                                            >
                                                <TableCell>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            color: isDisabled ? 'text.disabled' : 'text.primary',
                                                            fontWeight: isDisabled ? 'normal' : 'medium'
                                                        }}
                                                    >
                                                        {/* {rule.rule_type} */}
                                                        {formatRuleName(rule.rule_type)}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            color: isDisabled ? 'text.disabled' : 'text.primary'
                                                        }}
                                                    >
                                                        {formatRuleValue(rule)}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            color: isDisabled ? 'text.disabled' : 'text.primary'
                                                        }}
                                                    >
                                                        {rule.message}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={formatStatus(rule.status)}
                                                        size="small"
                                                        color={rule.status === 1 ? "success" : "default"}
                                                        variant={rule.status === 1 ? "filled" : "outlined"}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            color: isDisabled ? 'text.disabled' : 'text.secondary'
                                                        }}
                                                    >
                                                        {predefinedRule ? predefinedRule.description : 'No description available'}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    ) : (
                        <Typography variant="body2" color="textSecondary">
                            No rules defined for this notification.
                        </Typography>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="primary">
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default NotificationDetailModal;